<template>
  <div class="wealth-consult">
    <header class="page-header">
      <div>
        <h1><i class="fas fa-comments-dollar"></i> Wealth Advisor Consult</h1>
        <p class="subtitle">
          Ask questions about your book — portfolio exposure, client reviews,
          market event impact. Answers cite internal evidence only.
        </p>
      </div>
      <div class="controls">
        <label>
          Household
          <select v-model="selectedHouseholdId">
            <option :value="null">All households (book-wide)</option>
            <option v-for="h in households" :key="h.id" :value="h.id">
              {{ h.name }}
            </option>
          </select>
        </label>
        <label>
          Depth
          <select v-model="consultDepth">
            <option value="quick">Quick</option>
            <option value="deep">Deep</option>
          </select>
        </label>
      </div>
    </header>

    <div class="consult-shell">
      <aside class="sidebar">
        <h3>Sessions</h3>
        <button class="btn btn-small btn-primary" @click="newSession">
          <i class="fas fa-plus"></i> New chat
        </button>
        <ul class="session-list">
          <li
            v-for="s in sessions"
            :key="s.id"
            :class="{ active: session && session.id === s.id }"
            @click="loadSession(s.id)"
          >
            <div class="session-title">{{ s.title || 'Untitled' }}</div>
            <div class="session-meta">
              {{ s.turn_count }} turns · {{ formatRelative(s.updated_at) }}
            </div>
          </li>
          <li v-if="sessions.length === 0" class="empty">No sessions yet.</li>
        </ul>
      </aside>

      <main class="chat">
        <div class="turns" ref="turnsRef">
          <div
            v-for="(turn, idx) in visibleTurns"
            :key="idx"
            :class="['turn', 'turn-' + turn.role]"
          >
            <div class="turn-role">{{ turn.role === 'user' ? 'You' : 'Advisor AI' }}</div>
            <div class="turn-content" v-html="renderMarkdown(turn.content)"></div>
            <ul v-if="turn.evidence && turn.evidence.length" class="evidence-list">
              <li v-for="ev in turn.evidence" :key="ev.id">
                <span class="ev-type">{{ ev.type }}</span>
                <strong>{{ ev.title }}</strong>
                <span v-if="ev.snippet" class="ev-snippet"> — {{ ev.snippet }}</span>
              </li>
            </ul>
          </div>
          <div v-if="streaming && currentPhase" class="streaming-status">
            <i class="fas fa-spinner fa-spin"></i>
            {{ currentPhase.message || currentPhase.phase }}
          </div>
        </div>

        <form class="composer" @submit.prevent="submitQuery">
          <textarea
            v-model="queryText"
            rows="2"
            placeholder="Ask a question about your book…"
            :disabled="streaming"
            @keydown.meta.enter.prevent="submitQuery"
            @keydown.ctrl.enter.prevent="submitQuery"
          />
          <div class="composer-actions">
            <button type="submit" class="btn btn-primary" :disabled="streaming || !queryText.trim()">
              {{ streaming ? 'Streaming…' : 'Ask' }}
            </button>
          </div>
        </form>
      </main>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'WealthAdvisorConsult',
  data() {
    return {
      households: [],
      selectedHouseholdId: null,
      consultDepth: 'quick',
      sessions: [],
      session: null,
      queryText: '',
      streaming: false,
      currentPhase: null,
      pendingEvidence: [],
    }
  },
  computed: {
    visibleTurns() {
      return this.session ? this.session.turns || [] : []
    },
  },
  mounted() {
    this.loadHouseholds()
    this.loadSessions()
  },
  methods: {
    async loadHouseholds() {
      try {
        const res = await api.get('/wealth/households/')
        this.households = res.data.results || res.data || []
      } catch (err) {
        console.error('loadHouseholds failed', err)
      }
    },
    async loadSessions() {
      try {
        const res = await api.get('/wealth-consult/sessions/')
        this.sessions = res.data.results || []
      } catch (err) {
        console.error('loadSessions failed', err)
      }
    },
    async loadSession(sessionId) {
      try {
        const res = await api.get(`/wealth-consult/sessions/${sessionId}/`)
        this.session = res.data
      } catch (err) {
        console.error('loadSession failed', err)
      }
    },
    newSession() {
      this.session = null
    },
    renderMarkdown(text) {
      if (!text) return ''
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br/>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    },
    formatRelative(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      const diff = (Date.now() - d.getTime()) / 1000
      if (diff < 60) return 'just now'
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
      return d.toLocaleDateString()
    },
    async submitQuery() {
      const q = (this.queryText || '').trim()
      if (!q || this.streaming) return

      const userTurn = { role: 'user', content: q, evidence: [] }
      if (!this.session) {
        this.session = { id: null, turns: [], title: q.slice(0, 60) }
      }
      this.session.turns = [...(this.session.turns || []), userTurn]

      this.queryText = ''
      this.streaming = true
      this.currentPhase = { phase: 'starting', message: 'Starting…' }
      this.pendingEvidence = []

      const payload = {
        query: q,
        session_id: this.session.id || undefined,
        consult_depth: this.consultDepth,
        household_id: this.selectedHouseholdId || undefined,
      }

      try {
        const url = '/api/wealth-consult/ask-stream/'
        const resp = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify(payload),
        })
        if (!resp.ok || !resp.body) {
          throw new Error(`HTTP ${resp.status}`)
        }
        const reader = resp.body.getReader()
        const decoder = new TextDecoder('utf-8')
        let buffer = ''
        let streaming = true
        while (streaming) {
          const { value, done } = await reader.read()
          if (done) {
            streaming = false
            break
          }
          buffer += decoder.decode(value, { stream: true })
          const parts = buffer.split('\n\n')
          buffer = parts.pop() || ''
          for (const part of parts) {
            this.handleSSEChunk(part)
          }
        }
      } catch (err) {
        console.error('submitQuery stream error', err)
        this.session.turns.push({
          role: 'assistant',
          content: 'Sorry — the consult stream failed. Please retry.',
          evidence: [],
        })
      } finally {
        this.streaming = false
        this.currentPhase = null
        this.loadSessions()
      }
    },
    handleSSEChunk(chunk) {
      const dataLines = chunk
        .split('\n')
        .filter((l) => l.startsWith('data:'))
        .map((l) => l.slice(5).trim())
      if (!dataLines.length) return
      const raw = dataLines.join('\n')
      let evt
      try {
        evt = JSON.parse(raw)
      } catch {
        return
      }
      if (evt.type === 'status') {
        this.currentPhase = evt
      } else if (evt.type === 'result') {
        const data = evt.data || {}
        this.session = {
          id: data.session_id,
          turns: [
            ...(this.session.turns || []),
            {
              role: 'assistant',
              content: data.answer || '',
              evidence: data.evidence || [],
              actions: data.actions || [],
            },
          ],
          title: this.session.title,
        }
      } else if (evt.type === 'error') {
        this.session.turns.push({
          role: 'assistant',
          content: evt.message || 'Unknown error.',
          evidence: [],
        })
      }
    },
    getCsrfToken() {
      const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/)
      return m ? m[1] : ''
    },
  },
}
</script>

<style scoped>
.wealth-consult { padding: 24px; max-width: 1400px; margin: 0 auto; }
.page-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 24px; gap: 24px; flex-wrap: wrap; }
.subtitle { color: #666; margin: 4px 0 0 0; max-width: 680px; }
.controls { display: flex; gap: 16px; align-items: center; }
.controls label { display: flex; flex-direction: column; font-size: 12px; color: #6b7280; gap: 4px; }
.controls select { padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 6px; min-width: 180px; }

.consult-shell { display: grid; grid-template-columns: 280px 1fr; gap: 16px; min-height: 70vh; }
@media (max-width: 900px) { .consult-shell { grid-template-columns: 1fr; } }

.sidebar { background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; }
.sidebar h3 { margin: 0 0 8px 0; font-size: 14px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; }
.session-list { list-style: none; margin: 8px 0 0 0; padding: 0; max-height: 60vh; overflow: auto; }
.session-list li { padding: 8px; border-radius: 6px; cursor: pointer; }
.session-list li.active { background: #eff6ff; }
.session-list li:hover { background: #f9fafb; }
.session-title { font-size: 14px; font-weight: 500; }
.session-meta { font-size: 11px; color: #9ca3af; }
.session-list li.empty { color: #9ca3af; font-style: italic; }

.chat { display: flex; flex-direction: column; background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; }
.turns { flex: 1; padding: 16px; overflow-y: auto; min-height: 400px; }
.turn { margin-bottom: 20px; }
.turn-role { font-size: 11px; color: #6b7280; text-transform: uppercase; font-weight: 600; margin-bottom: 4px; }
.turn-content { background: #f9fafb; border-radius: 8px; padding: 12px 16px; line-height: 1.55; font-size: 14px; }
.turn-user .turn-content { background: #e0e7ff; }
.evidence-list { margin: 8px 0 0 0; padding-left: 0; list-style: none; font-size: 12px; color: #4b5563; }
.evidence-list li { padding: 6px 0; border-top: 1px dashed #e5e7eb; }
.ev-type { display: inline-block; font-size: 10px; background: #e5e7eb; color: #374151; padding: 1px 6px; border-radius: 4px; margin-right: 6px; text-transform: uppercase; }
.ev-snippet { color: #6b7280; }

.streaming-status { font-size: 13px; color: #2563eb; padding: 12px 16px; background: #eff6ff; border-radius: 6px; margin-bottom: 8px; }

.composer { border-top: 1px solid #e5e7eb; padding: 12px; background: #fafafa; }
.composer textarea { width: 100%; padding: 8px 10px; border: 1px solid #d1d5db; border-radius: 6px; resize: vertical; font-family: inherit; font-size: 14px; }
.composer-actions { display: flex; justify-content: flex-end; margin-top: 8px; }

.btn { padding: 6px 12px; border: 1px solid #d1d5db; border-radius: 6px; background: #fff; cursor: pointer; font-size: 13px; }
.btn-primary { background: #2563eb; color: #fff; border-color: #2563eb; }
.btn-small { padding: 4px 8px; font-size: 12px; }
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
